import { extend, findIndex } from "@dhx/ts-common/core";
import { el } from "@dhx/ts-common/dom";
import { EventSystem } from "@dhx/ts-common/events";
import { locate } from "@dhx/ts-common/html";
import { addHotkeys } from "@dhx/ts-common/Keymanager";
import { ICell, Layout, LayoutEvents, ILayoutEventHandlersMap } from "@dhx/ts-layout";
import { ITabbarConfig, RenderMode, TabbarEvents, ITabbarEventHandlersMap, ITabbar } from "./types";

export class Tabbar extends Layout implements ITabbar {
	public config: ITabbarConfig;
	public events: EventSystem<TabbarEvents | LayoutEvents, ITabbarEventHandlersMap | ILayoutEventHandlersMap>;
	private _hotkeysDestructor: () => void;

	constructor(container, config: ITabbarConfig) {
		super(container, extend({
			mode: RenderMode.top,
			tabWidth: 200,
			tabHeight: 45,
		}, config));
		this.events = new EventSystem<TabbarEvents>(this);
	}
	toVDOM(): any {
		let activeView = null;
		if (!this.config.noContent) {
			activeView = this.getCell(this.config.activeView);
			if (activeView) {
				activeView.config.css = activeView.config.css || "";
			}
		}
		return el("div", {
			class: "dhx_widget dhx_tabbar" +
				(this.config.mode ? " dhx_tabbar--" + this.config.mode : "") +
				(this.config.css ? " " + this.config.css : "")
		}, [
			this._drawTabs(),
			activeView ? activeView.toVDOM() : null
		]);
	}
	destructor(): void {
		this._hotkeysDestructor();
		super.destructor();
	}
	getWidget(): any {
		const activeCell = this._cells.filter(cell => this.getActive() === cell.id);
		return activeCell[0].getWidget();
	}
	removeCell(id: string): void {
		if (id === this.config.activeView) {
			const cellLength = this._cells.length;
			let index = findIndex(this._cells, cell => cell.id === this.config.activeView);
			if (index < 0) {
				return;
			}
			if (index === cellLength - 1) {
				index = index - 1;
			}
			super.removeCell(id);
			if (cellLength === 1) {
				this.config.activeView = null;
			} else {
				this.setActive(this._cells[index].id);
			}
		} else {
			super.removeCell(id);
		}
		this.events.fire(TabbarEvents.close, [id]);
	}
	setActive(id: string): void {
		const prev = this.config.activeView;
		this.getCell(id).show();
		this._focusTab(id);
		this.events.fire(TabbarEvents.change, [id, prev]);
	}
	getActive(): string {
		return this.config ? this.config.activeView : null;
	}
	protected _initHandlers() {
		super._initHandlers();
		this._handlers = {
			...this._handlers,
			onTabClick: e => {
				const tabId = locate(e, "dhx_tabid");
				if (!tabId) {
					return;
				}
				const prev = this.config.activeView;

				if (e.target.classList.contains("dhx_tabbar-tab__close")) {
					this.removeCell(tabId);
				} else {
					this.config.activeView = tabId;
					this.events.fire(TabbarEvents.change, [this.config.activeView, prev]);
				}
				this.paint();
			}
		};
		const activeNextTab = (e) => {
			e.preventDefault();
			const activeIndex = findIndex(this._cells, cell => cell.id === this.config.activeView);
			const prev = this.config.activeView;
			if (activeIndex === -1) {
				return;
			}
			if (activeIndex === this.config.views.length - 1) {
				this.config.activeView = this._cells[0].id;
			} else {
				this.config.activeView = this._cells[activeIndex + 1].id;
			}
			this.events.fire(TabbarEvents.change, [this.config.activeView, prev]);
			this._focusTab(this.config.activeView);
			this.paint();
		};
		const activePrevTab = (e) => {
			e.preventDefault();
			const activeIndex = findIndex(this._cells, cell => cell.id === this.config.activeView);
			const prev = this.config.activeView;
			if (activeIndex === -1) {
				return;
			}
			if (activeIndex === 0) {
				this.config.activeView = this._cells[this.config.views.length - 1].id;
			} else {
				this.config.activeView = this._cells[activeIndex - 1].id;
			}
			this.events.fire(TabbarEvents.change, [this.config.activeView, prev]);
			this._focusTab(this.config.activeView);
			this.paint();
		};
		const isVertical = this.config.mode === "right" || this.config.mode === "left";
		this._hotkeysDestructor = addHotkeys(
			{
				arrowright: activeNextTab,
				arrowup: isVertical ? activePrevTab : activeNextTab,
				arrowleft: activePrevTab,
				arrowdown: isVertical ? activeNextTab : activePrevTab,
			}, () => locate(document.activeElement, "tabs_id") === this._uid
		);
	}
	private _focusTab(id: string) {
		setTimeout(() => {
			this.getRootView().refs[id].el.focus();
		}, 10);
	}
	private _getIndicatorPosition() {
		let activeIndex = findIndex(this._cells, cell => cell.id === this.config.activeView);
		if (activeIndex === -1) {
			activeIndex = 0;
		}
		switch (this.config.mode) {
			case RenderMode.bottom:
			case RenderMode.top:
				return {
					transform: `translateX(${this.config.tabWidth * activeIndex + "px"})`,
					width: this.config.tabWidth + "px",
					height: "2px"
				};
			case RenderMode.left:
			case RenderMode.right:
				return {
					transform: `translateY(${this.config.tabHeight * activeIndex + "px"})`,
					height: this.config.tabHeight + "px",
					width: "2px"
				};
		}
	}
	private _drawTabs() {
		if (!this._cells.length) { return null; }

		const tabStyle = this.config.tabWidth ? {
			width: this.config.tabWidth + "px",
			height: this.config.tabHeight + "px",
		} : null;
		return el(".dhx_tabbar-header__wrapper", [
			el(".dhx_tabbar-header-active", {
				style: this._getIndicatorPosition()
			}),
			el("ul" + "." + this.config.mode, {
				tabs_id: this._uid,
				class: "dhx_tabbar-header ",
				onclick: this._handlers.onTabClick
			}, [
				...this._cells.map((cell: ICell) => {
					return el("li", {
						class: "dhx_tabbar-tab" + (cell.config.tabCss ? " " + cell.config.tabCss : ""),
						dhx_tabid: cell.id,
						role: "presentation",
					}, [
						el("button.dhx_button.dhx_tabbar-tab-button" + (this.config.activeView === cell.id ? ".dhx_tabbar-tab-button--active" : ""), {
							"tabindex": "0",
							"style": tabStyle,
							"aria-controls": cell.id,
							"id": "tab-content-" + cell.id,
							"aria-selected": `${this.config.activeView === cell.id}`,
							"_ref": cell.id,
						}, [el("span.dhx_button__text", cell.config.tab)]),
						this.config.closeButtons
							? el("div.dhx_tabbar-tab__close.dxi--small.dxi.dxi-close", {
								"tabindex": 0,
								"role": "button",
								"aria-pressed": "false"
							})
							: null
					]
					);
				})])
		]);
	}
}